/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: IVRStatsPage.tsx
 🗓️ Created: 2025-12-03
 🧩 Description:
    IVR Statistics page showing usage analytics and reporting.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, PieChart, TrendingUp, Clock, PhoneCall, Download, ChevronDown, ChevronUp, List, Filter, RefreshCw } from 'lucide-react';

import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import Select from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import DateRangePicker from '../../components/ui/DateRangePicker';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { IVRStatsSummary, IVRStatRecord } from './types';
import { exportSummaryToCSV, exportDetailsToCSV as exportDetailsFn } from './ivrStatsExport';
import { IVRCallDetailsSection } from './components/IVRCallDetailsSection';



export default function IVRStatsPage() {
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Effective domain ID for contextual filtering
  const effectiveDomainId = selectedDomain?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [availableIVRs, setAvailableIVRs] = useState<any[]>([]);
  const [selectedIVR, setSelectedIVR] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    startTime: '00:00',
    endTime: '23:59',
  });
  const [stats, setStats] = useState<IVRStatsSummary | null>(null);

  // Call details state
  const [showDetails, setShowDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [callDetails, setCallDetails] = useState<IVRStatRecord[]>([]);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [detailsPage, setDetailsPage] = useState(0);
  const [detailsLimit] = useState(50);
  const [actionFilter, setActionFilter] = useState<string>('all');

  // Load all IVRs (not just those with stats_enabled=true)
  const loadIVRs = useCallback(async () => {
    try {
      const domainParam = effectiveDomainId ? `&domainId=${effectiveDomainId}` : '';
      const response = await fetch(
        buildApiUrl(`/api/telephony/ivrs?limit=100&sortBy=name&sortOrder=asc${domainParam}`),
        {
          method: 'GET',
          headers: getApiHeaders()
        }
      );

      if (response.ok) {
        const result = await response.json();
        setAvailableIVRs(result.data || []);
        if (result.data && result.data.length > 0 && !selectedIVR) {
          setSelectedIVR(result.data[0].id);
        }
      } else {
        showError(`Failed to load IVRs (${response.status})`);
      }
    } catch (error) {
      showError(error.message || 'Failed to load IVRs');
    }
  }, [selectedIVR, effectiveDomainId, showError]);

  // Load stats for selected IVR
  const loadStats = useCallback(async () => {
    if (!selectedIVR) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        ivr_id: selectedIVR.toString(),
        start_date: `${dateRange.startDate}T${dateRange.startTime || '00:00'}:00`,
        end_date:   `${dateRange.endDate}T${dateRange.endTime   || '23:59'}:59`,
      });

      if (effectiveDomainId) {
        params.append('domain_id', effectiveDomainId.toString());
      }

      const response = await fetch(
        buildApiUrl(`/api/logs/ivr-stats/summary?${params}`),
        {
          method: 'GET',
          headers: getApiHeaders()
        }
      );

      if (response.ok) {
        const result = await response.json();
        setStats(result.data);
      } else {
        showError(`Failed to load IVR statistics (${response.status})`);
      }
    } catch (error) {
      showError(error.message || 'Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  }, [selectedIVR, dateRange, effectiveDomainId, showError]);

  useEffect(() => {
    loadIVRs();
  }, [loadIVRs, effectiveDomainId]);

  useEffect(() => {
    if (selectedIVR) {
      loadStats();
    }
  }, [selectedIVR, loadStats]);

  // Load call details
  const loadCallDetails = useCallback(async () => {
    if (!selectedIVR) return;

    setDetailsLoading(true);
    try {
      const params = new URLSearchParams({
        ivr_id: selectedIVR.toString(),
        start_date: `${dateRange.startDate}T${dateRange.startTime || '00:00'}:00`,
        end_date:   `${dateRange.endDate}T${dateRange.endTime   || '23:59'}:59`,
        limit: detailsLimit.toString(),
        offset: (detailsPage * detailsLimit).toString()
      });

      if (actionFilter && actionFilter !== 'all') {
        params.append('action_type', actionFilter);
      }

      if (effectiveDomainId) {
        params.append('domain_id', effectiveDomainId.toString());
      }

      const response = await fetch(
        buildApiUrl(`/api/logs/ivr-stats/details?${params}`),
        {
          method: 'GET',
          headers: getApiHeaders()
        }
      );

      if (response.ok) {
        const result = await response.json();
        setCallDetails(result.data.records);
        setDetailsTotal(result.data.total);
      } else {
        showError(`Failed to load call details (${response.status})`);
      }
    } catch (error) {
      showError(error.message || 'Failed to load call details');
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedIVR, dateRange, detailsPage, detailsLimit, actionFilter, effectiveDomainId, showError]);

  useEffect(() => {
    if (showDetails && selectedIVR) {
      loadCallDetails();
    }
  }, [showDetails, selectedIVR, detailsPage, actionFilter, loadCallDetails]);

  // Export summary to CSV
  const exportToCSV = useCallback(() => {
    exportSummaryToCSV(stats, dateRange, showSuccess, showError);
  }, [stats, dateRange, showError, showSuccess]);

  // Export call details to CSV
  const exportDetailsToCSV = useCallback(() => {
    exportDetailsFn(callDetails, actionFilter, dateRange, showSuccess, showError);
  }, [callDetails, actionFilter, dateRange, showError, showSuccess]);

  return (
    <div className="p-6 pb-20">
      {/* Header with Export Button */}
      <ModuleFormHeader
        icon={<BarChart3 className="h-5 w-5 text-primary" />}
        title={t('ivrStats:title') || 'IVR Statistics'}
        backButtonText=""
        onBack={() => { }}
        hideBackButton={true}
      >
        {stats && stats.totalInteractions > 0 && (
          <Button onClick={exportToCSV} size="sm">
            <Download className="h-4 w-4 mr-2" />
            {t('ivrStats:actions.export') || 'Export Summary'}
          </Button>
        )}
      </ModuleFormHeader>

      <div className="mt-8 pb-24 space-y-4">

        {/* Filters */}
        <FormBox
          title={t('ivrStats:sections.filters') || 'Filters'}
          icon={<Filter className="h-5 w-5" />}
        >
          {/* Single row: IVR Menu | Select | Date Range | DateRangePicker */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <Label className="text-left">
              {t('ivrStats:filters.ivr') || 'IVR Menu'}
            </Label>
            <Select
              value={selectedIVR?.toString() || ''}
              onValueChange={(value) => setSelectedIVR(Number(value))}
              options={availableIVRs.map(ivr => ({
                value: ivr.id.toString(),
                label: ivr.name
              }))}
            />
            <Label className="text-left">
              {t('ivrStats:filters.dateRange') || 'Date Range'}
            </Label>
            <DateRangePicker
              value={dateRange}
              onChange={(range) => setDateRange({ startTime: '00:00', endTime: '23:59', ...range })}
            />
          </div>
        </FormBox>


        {/* Statistics Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : stats && stats.totalInteractions > 0 ? (
          <>
            {/* Summary Cards */}
            <FormBox
              title={t('ivrStats:sections.summary') || 'Summary'}
              icon={<TrendingUp className="h-5 w-5" />}
            >
              <div className="grid grid-cols-6 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/30 border">
                  <PhoneCall className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">{t('ivrStats:cards.totalInteractions') || 'Total'}</p>
                  <p className="text-xl font-bold">{stats.totalInteractions.toLocaleString()}</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-muted/30 border">
                  <TrendingUp className="h-6 w-6 text-green-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">{t('ivrStats:cards.optionPresses') || 'Options'}</p>
                  <p className="text-xl font-bold">{stats.optionPresses.toLocaleString()}</p>
                  <p className="text-xs text-green-600">{((stats.optionPresses / stats.totalInteractions) * 100).toFixed(1)}%</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-muted/30 border">
                  <Clock className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">{t('ivrStats:cards.timeouts') || 'Timeouts'}</p>
                  <p className="text-xl font-bold">{stats.timeouts.toLocaleString()}</p>
                  <p className="text-xs text-yellow-600">{((stats.timeouts / stats.totalInteractions) * 100).toFixed(1)}%</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-muted/30 border">
                  <BarChart3 className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">{t('ivrStats:cards.failures') || 'Failures'}</p>
                  <p className="text-xl font-bold">{stats.failures.toLocaleString()}</p>
                  <p className="text-xs text-red-600">{((stats.failures / stats.totalInteractions) * 100).toFixed(1)}%</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-muted/30 border">
                  <PhoneCall className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">Direct Dials</p>
                  <p className="text-xl font-bold">{stats.directDials.toLocaleString()}</p>
                  <p className="text-xs text-blue-600">{((stats.directDials / stats.totalInteractions) * 100).toFixed(1)}%</p>
                </div>

                <div className="text-center p-4 rounded-lg bg-muted/30 border">
                  <BarChart3 className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">Invalid</p>
                  <p className="text-xl font-bold">{stats.invalidInputs.toLocaleString()}</p>
                  <p className="text-xs text-orange-600">{((stats.invalidInputs / stats.totalInteractions) * 100).toFixed(1)}%</p>
                </div>
              </div>
            </FormBox>

            {/* Timing & Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormBox
                title={t('ivrStats:sections.timing') || 'Timing Statistics'}
                icon={<Clock className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  <Label className="text-left">{t('ivrStats:labels.avgWaitTime') || 'Avg Wait'}</Label>
                  <div className="font-semibold">{stats.averageWaitTime?.toFixed(1) || 0}s</div>
                  <Label className="text-left">{t('ivrStats:labels.avgDuration') || 'Avg Duration'}</Label>
                  <div className="font-semibold">{stats.averageDuration?.toFixed(1) || 0}s</div>
                </div>
              </FormBox>

              <FormBox
                title={t('ivrStats:sections.mostPressed') || 'Most Pressed Digit'}
                icon={<TrendingUp className="h-5 w-5" />}
              >
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-1">{stats.mostPressedDigit || '-'}</div>
                  <p className="text-sm text-muted-foreground">{stats.mostPressedCount?.toLocaleString() || 0} {t('ivrStats:labels.presses') || 'presses'}</p>
                </div>
              </FormBox>

              <FormBox
                title="Success Metrics"
                icon={<TrendingUp className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  <Label className="text-left">Success Rate</Label>
                  <div className="font-semibold text-green-600">{((stats.optionPresses / stats.totalInteractions) * 100).toFixed(1)}%</div>
                  <Label className="text-left">Failure Rate</Label>
                  <div className="font-semibold text-red-600">{(((stats.failures + stats.timeouts) / stats.totalInteractions) * 100).toFixed(1)}%</div>
                </div>
              </FormBox>
            </div>

            {/* Digit Breakdown */}
            <FormBox
              title={t('ivrStats:sections.digitBreakdown') || 'Digit Press Breakdown'}
              icon={<BarChart3 className="h-5 w-5" />}
            >
              <div className="space-y-3">
                {stats.digitBreakdown && stats.digitBreakdown.length > 0 ? (
                  stats.digitBreakdown.map((item) => (
                    <div key={item.digit} className="flex items-center gap-4">
                      <div className="w-12 text-center font-mono font-bold text-lg">{item.digit}</div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-muted-foreground">{item.count.toLocaleString()} presses</span>
                          <span className="text-sm font-semibold">{item.percentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    {t('ivrStats:messages.noData') || 'No statistics available for this period'}
                  </p>
                )}
              </div>
            </FormBox>

            <IVRCallDetailsSection
              showDetails={showDetails}
              onToggleDetails={() => setShowDetails(!showDetails)}
              detailsTotal={detailsTotal}
              detailsLoading={detailsLoading}
              callDetails={callDetails}
              actionFilter={actionFilter}
              onActionFilterChange={setActionFilter}
              detailsPage={detailsPage}
              detailsLimit={detailsLimit}
              onPageChange={setDetailsPage}
              onExport={exportDetailsToCSV}
              t={t}
            />
          </>
        ) : selectedIVR ? (
          <FormBox
            title={t('ivrStats:messages.noData') || 'No Data'}
            icon={<PieChart className="h-5 w-5" />}
          >
            <div className="text-center py-12">
              <PieChart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t('ivrStats:messages.noData') || 'No statistics available for this period'}
              </h3>
              <p className="text-muted-foreground mb-4">
                There are no IVR interactions recorded for the selected date range.
              </p>
              <p className="text-sm text-muted-foreground">
                {stats ? `Selected: ${stats.ivrName}` : 'Loading...'}
              </p>
            </div>
          </FormBox>
        ) : (
          <FormBox
            title={t('ivrStats:messages.noIVRSelected') || 'Select an IVR'}
            icon={<PieChart className="h-5 w-5" />}
          >
            <div className="text-center py-12">
              <PieChart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('ivrStats:messages.noIVRSelected') || 'Select an IVR to view statistics'}</h3>
              <p className="text-muted-foreground">
                {t('ivrStats:messages.noIVRSelectedDesc') || 'Choose an IVR menu from the dropdown above to see its usage statistics'}
              </p>
            </div>
          </FormBox>
        )}
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSave={loadStats}
        saveText={
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            {isLoading ? t('common:loading') || 'Loading...' : t('common:refresh') || 'Refresh'}
          </>
        }
        onReset={stats && stats.totalInteractions > 0 ? exportToCSV : undefined}
        resetText={
          <>
            <Download className="h-4 w-4 mr-2" />
            {t('ivrStats:actions.export') || 'Export Summary'}
          </>
        }
        showCancel={false}
        showDelete={false}
        isSaving={isLoading}
        saveDisabled={!selectedIVR}
      />
    </div>
  );
}
